/**
 * Student Portal Web-Push helper.
 * Uses the student token (via the passed studentApi axios instance) and the
 * /student-auth/push/* endpoints. Mirrors the staff notificationService but is
 * fully self-contained so the student portal can opt into browser alerts.
 */

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

export const pushSupported = () =>
  'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

export const pushPermission = () =>
  pushSupported() ? Notification.permission : 'unsupported';

async function getRegistration() {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  const reg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  return reg;
}

export async function isStudentSubscribed() {
  try {
    if (!pushSupported()) return false;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}

/** Subscribe the student to web-push. Returns true on success. */
export async function subscribeStudentPush(studentApi) {
  if (!pushSupported()) throw new Error('Push notifications are not supported on this device.');

  if (Notification.permission === 'denied') {
    throw new Error('Notifications are blocked. Please enable them in your browser settings.');
  }
  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Permission denied.');

  const reg = await getRegistration();
  const { data } = await studentApi.get('/api/student-auth/push/vapid-public-key');
  const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }

  await studentApi.post('/api/student-auth/push/subscribe', {
    subscription: sub.toJSON(),
    device_type: /android|iphone|ipad|mobile/i.test(navigator.userAgent) ? 'mobile' : 'web',
    device_name: navigator.userAgent.slice(0, 60),
  });
  return true;
}

export async function unsubscribeStudentPush(studentApi) {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;
    await studentApi.post('/api/student-auth/push/unsubscribe', { endpoint: sub.endpoint });
    await sub.unsubscribe();
    return true;
  } catch {
    return false;
  }
}
